import Dexie, { Table } from 'dexie';

type UploadState = {
    state: 'UPLOADING' | 'UPLOADED' | 'FAILED';
    uploadedBytes: number;
    totalBytes: number;
    sessionUrl?: string;   // resumable session URL
    updatedAt: number;
    uploadProgressPercent: number;
};

type AssetRow = {
    assetId: string;
    assetType: string;
    title: string;
    status: string;
    deleted: boolean;
    projectId: string;
    thumbnailId: string;

    // local storage
    localPresent: boolean;         // true once OPFS write completes
    opfsPath: string;              // e.g. /assets/{assetId}/original
    objectUrl?: string;
    upload: UploadState;
    updatedAt: number;
};

type TrackRow = {
    assetId: string;
    audioBitrate?: number;
    createTime: number;
    deleted?: boolean;
    duration?: string;
    projectId: string;
    height?: number;
    width?: number;
    sampleRate?: number;
    title: string;
    trackId: string;
    trackType?: number;
    userId?: string;
    videoBitrate?: number;

    // local storage
    localPresent: boolean;         // true once OPFS write completes
    opfsPath: string;              // e.g. /assets/{assetId}/original
    objectUrl?: string;
    upload: UploadState;
    updatedAt: number;

    thumbnailOpfsPath?: string;
}

class LocalDB extends Dexie {
    assets!: Table<AssetRow, string>;
    tracks!: Table<TrackRow, string>;
    constructor() {
        super('LocalAssetDB');
        this.version(1).stores({
            assets: 'assetId',
            tracks: 'trackId',
        });
    }
}
const db = new LocalDB();


async function GetRootDir(): Promise<FileSystemDirectoryHandle> {
    return await navigator.storage.getDirectory();
}

async function WriteFileToOPFS(path: string, file: File): Promise<FileSystemFileHandle> {
    // path: e.g. /tracks/{id}/original
    const segments = path.split('/').filter(Boolean); // remove leading slash
    let dir = await GetRootDir();
    for (let i = 0; i < segments.length - 1; i++) {
        dir = await dir.getDirectoryHandle(segments[i], { create: true });
    }
    const fileName = segments[segments.length - 1];
    const fileHandle = await (dir as FileSystemDirectoryHandle).getFileHandle(fileName, { create: true });
    const writable = await fileHandle.createWritable();
    await file.stream().pipeTo(writable);
    return fileHandle;
}

async function GetFileFromOPFS(path: string): Promise<File> {
    const segments = path.split('/').filter(Boolean);
    let dir = await GetRootDir();
    for (let i = 0; i < segments.length - 1; i++) {
        dir = await dir.getDirectoryHandle(segments[i], { create: false });
    }
    const fileName = segments[segments.length - 1];
    const fh = await dir.getFileHandle(fileName, { create: false });
    return await fh.getFile();
}

class LocalAssetStoreInstance {

    public CreateAsset = ({}) => {

    }

    public CreateTrack = async ({trackId, assetId, projectId, title, file, createTime} : {
        trackId: string;
        assetId: string;
        projectId: string;
        title: string;
        file: File;
        createTime: number | Date;
    }) => {
        const opfsPath = `/tracks/${trackId}/original`;
        await WriteFileToOPFS(opfsPath, file);
        const now = Date.now();

        const trackRow = {
            assetId,
            trackId,
            createTime: typeof createTime === "number" ? createTime : +createTime, // ensure number
            projectId,
            title,
            localPresent: true,
            opfsPath: opfsPath,
            upload: {
                state: "UPLOADING",
                uploadedBytes: 0,
                totalBytes: file.size,
                updatedAt: now,
                uploadProgressPercent: 0,
            },
            updatedAt: now,
        } satisfies TrackRow;

        await db.tracks.put(trackRow);

        const localFile = await GetFileFromOPFS(opfsPath);
        const objectUrl = URL.createObjectURL(localFile);

        await db.tracks.update(trackId, { objectUrl, updatedAt: Date.now() });

        return { objectUrl };
    }

    public GetThumbnail = ({trackId}) => {


    }


}

const LocalAssetStore = new LocalAssetStoreInstance();

export {LocalAssetStore};